import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Event, NavigationExtras, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { RootPageHelper } from '@app/app.root.page.helper';
import { AppError, ErrorMessages, ErrorTypes } from '@app/entities/models/error/error.model';
import { PtrabSessionManager } from '@app/services/ptrab-session.manager.service';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';
import { ENV } from '@environments/environment';
import { AlertService } from '@services/alert/alert.service';
import { AppManagerService } from '@services/app/app.manager.service';
import { AUTHENTICATION_STATUS, AuthService } from '@services/auth/auth.service';
import { ErrorService } from '@services/error/error.service';
import { LanguageService } from '@services/language/language.service';
import { LoadingService } from '@services/loading/loading.service';
import { Logger } from '@services/logger/logger.service';
import { MessageService } from '@services/message/message.service';
import { ModalManager } from '@services/modal-manager/modal-manager';
import { NavigationHelperService } from '@services/navigation/navigation.helper.service';
import { NavigationEvent, NavigationEvents, NavigationService } from '@services/navigation/navigation.service';
import { StorageService } from '@services/storage';
import { UserSyncPropsService } from '@services/user/user-sync-props.service';
import { NotificationHandler } from '@shared/helpers/notification/notification.handler';
import { RouteHelper } from '@shared/helpers/route.helper';
import { ActionCookies, COOKIES_TIME, CookiesConfig, UserCookies } from '@shared/models/cookies/cookies.config';
import { WebViewService } from '@shared/services/webview.service';
import { domChanges } from '@shared/utils/utils';

import { MTranslateService } from '@mercadona/core/translate';

import { WebSocketManagerService } from './presentation/services/web-socket-manager.service';

import { AlertComponent } from '@/components/alert/alert.component';
import { LightboxCookiesComponent } from '@/components/lightbox-cookies/lightbox-cookies.component';
import { TourComponent } from '@/components/tour/tour.component';
import { PAGES } from '@/enums/pages.enum';
import { HomeType, UserHome, UserLoginType } from '@/enums/user/user.enum';
import {
  FirebaseAnalyticsUseCases,
  FIREBASE_ANALYTICS_USE_CASES
} from '@/use-cases/firebase-analytics/firebase-analytics.use-cases.contract';
import { NETWORK_USE_CASES, NetworkUseCases } from '@/use-cases/network/network.use-cases.contract';
import { NotificationUseCases, NOTIFICATION_USE_CASES } from '@/use-cases/notification/notification.use-cases.contract';
import { TourUseCases, TOUR_USE_CASES } from '@/use-cases/tour/tour.use-cases.contract';
import { USER_USE_CASES, UserUseCases } from '@/use-cases/user/user.use-cases.contract';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [RouterOutlet, LightboxCookiesComponent, AlertComponent, TourComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly logger: Logger = new Logger('AppComponent');
  private redirectOnLoginChange = true;
  userBasicCookies!: boolean;
  compWindow: MSafeAny = window;
  subsUserBasicCookiesSubject!: Subscription;
  trackedTO!: boolean;
  showCookies!: boolean;
  closeOutsideDisabled!: boolean;

  #userUseCases: UserUseCases = inject(USER_USE_CASES);
  #notificationUseCases: NotificationUseCases = inject(NOTIFICATION_USE_CASES);
  #webSocketManagerService: WebSocketManagerService = inject(WebSocketManagerService);
  #firebaseAnalyticsUseCases: FirebaseAnalyticsUseCases = inject(FIREBASE_ANALYTICS_USE_CASES);
  #tourUseCases: TourUseCases = inject(TOUR_USE_CASES);
  #cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private network: NetworkUseCases = inject(NETWORK_USE_CASES);
  private translateService: MTranslateService = inject(MTranslateService);

  constructor(
    private appManagerService: AppManagerService,
    private authService: AuthService,
    private errorService: ErrorService,
    private language: LanguageService,
    private loadingService: LoadingService,
    private location: Location,
    private messageService: MessageService,
    private modalManager: ModalManager,
    private navigationService: NavigationService,
    private navigationHelperService: NavigationHelperService,
    private ptrabSessionManager: PtrabSessionManager,
    private router: Router,
    private storageService: StorageService,
    private alertService: AlertService,
    private userSyncPropsService: UserSyncPropsService,
    private cookieConfig: CookiesConfig,
    private webViewService: WebViewService
  ) {}

  ngOnDestroy() {
    this.unsubscribeShowCookies();
  }

  ngOnInit() {
    this.initializeApp();
    this.handleDeprecatedUrls();
    this.#firebaseAnalyticsUseCases.logAppStartupEvent();
  }

  async initializeApp() {
    this.initLogger();
    this.subscribeToNavigationMessages();
    this.subscribeToErrors();
    this.language.init();
    this.appManagerService.init(this.ptrabSessionManager);
    await this.determineRootPage();

    window.onfocus = async () => {
      await this.#userUseCases.checkData();
    };

    this.subscribeToUserStatus();
  }

  // Rewrite and force reload on Ionic3 urls
  handleDeprecatedUrls() {
    this.router.events.pipe(take(1)).subscribe((value) => {
      if (this.isDeprecatedUrl(value)) {
        window.location.href = this.rewriteDeprecatedUrl((value as NavigationStart).url);
      }
    });
  }

  private isDeprecatedUrl(value: Event) {
    return (
      value instanceof NavigationStart && value?.url?.match(/^\/#/) && !RouteHelper.isAdfsPostLoginRedirect(value?.url)
    );
  }

  private rewriteDeprecatedUrl(url: string) {
    return url
      .replace('/#', '')
      .replace(/javascript:.*/i, '') // prevent script injection on url
      .replace('/contents', '')
      .replace('menu-', '')
      .replace('mercadona-publications', 'publications')
      .replace('sniffer', 'device-info')
      .replace('ptrab/personal-income-tax', 'ptrab/irpf')
      .replace('profile/personal-data', 'personal-data');
  }

  private initLogger() {
    if (!ENV.isDebugMode) {
      Logger.enableProductionMode();
    }
  }

  private subscribeShowCookies() {
    this.subsUserBasicCookiesSubject = this.cookieConfig.userBasicCookies$.subscribe((status) => {
      this.userBasicCookies = status;
    });
  }

  private unsubscribeShowCookies() {
    if (this.subsUserBasicCookiesSubject) {
      this.subsUserBasicCookiesSubject.unsubscribe();
    }
  }

  async checkIsExpiredCookies() {
    const notStoredCookie = await this.cookieConfig.checkIsExpiredCookiesLocal();
    if (!notStoredCookie) {
      this.userBasicCookies = true;
    } else if (notStoredCookie?.action === ActionCookies.RENEW) {
      this.renewCookieIos();
    } else {
      this.removeCookies();
    }
  }

  manageActionCookies(event: MSafeAny) {
    switch (event.action) {
      case ActionCookies.ACCEPT:
      case ActionCookies.SAVE:
        this.saveCookies(!event.cookies ? this.acceptAllCookies : event.cookies);
        break;
      case ActionCookies.REJECT:
        this.saveCookies(this.rejectCookies);
        break;
      case ActionCookies.REMOVE:
        this.removeCookies();
        break;
    }
  }

  get acceptAllCookies() {
    const cookies: UserCookies = {
      essentialsCookies: true,
      notEssentialsCookies: true,
      expireTimeInMilliseconds: COOKIES_TIME.expireTime
    };

    return cookies;
  }

  get rejectCookies() {
    const cookies: UserCookies = {
      essentialsCookies: true,
      notEssentialsCookies: false,
      expireTimeInMilliseconds: COOKIES_TIME.expireTime
    };
    return cookies;
  }

  removeCookies() {
    this.cookieConfig.removeCookies();
    this.userBasicCookies = false;
  }

  saveCookies(cookies: UserCookies, expiredTime?: Date) {
    this.cookieConfig.saveCookies(cookies, expiredTime);
    this.userBasicCookies = true;
    const currentUrl = this.router.url;
    this.router.navigate([currentUrl]).then(() => {
      this.compWindow.location.reload();
    });
  }

  async renewCookieIos() {
    const cookieResult = await this.cookieConfig.renewCookie();
    if (cookieResult?.action === ActionCookies.SAVE) {
      const cookieData = cookieResult.data;
      this.saveCookies(cookieData.cookies, cookieData.expiredTime);
    } else {
      this.removeCookies();
    }
  }

  showLightBoxCookies() {
    const isLogged = this.authService.isLoggedIn();
    const noBasicCookiesAcceptance = !this.userBasicCookies;
    if (!this.trackedTO && isLogged && noBasicCookiesAcceptance && !this.webViewService.isWebView()) {
      this.trackedTO = true;
    }
    this.showCookies = isLogged && noBasicCookiesAcceptance && !this.webViewService.isWebView();
  }

  subscribeToNavigationMessages() {
    this.navigationService.navigationEvent$.subscribe(async (navigationEvent: NavigationEvent) => {
      await this.appManagerService.handleNavigationEvent(navigationEvent);
      this.checkIsExpiredCookies();
    });
  }

  subscribeToUserStatus() {
    this.authService.loginChanged$.subscribe(() => {
      if (this.authService.getStatus() === AUTHENTICATION_STATUS.LOGGED_ADFS) {
        this.authService.initUserInfo();
      } else if (this.authService.getStatus() === AUTHENTICATION_STATUS.LOGGED_ACTIVO2) {
        this.onLogged();
        this.subscribeShowCookies();
      } else if (this.authService.getStatus() === AUTHENTICATION_STATUS.LOGOUT) {
        this.onLogout();
      }
    });
  }

  subscribeToErrors() {
    this.errorService.error$.subscribe(async (error: AppError) => {
      this.logger.error('subscribeToErrors error:', error);
      const activePage = this.router.url.replace('/', '');
      this.modalManager.dismissAllMatModal();

      switch (error?.type) {
        case ErrorTypes.MAINTENANCE_MODE: // Maintenance
          return this.manageMaintenanceMode(activePage);

        case ErrorTypes.USER_BANNED: // Banned
          return this.manageUserBanned(activePage);
        // Forbidden
        case ErrorTypes.FORBIDDEN:
          await this.determineRootPage(error.params);
          return null;

        case ErrorTypes.TERMS_NOT_ACCEPTED:
          return this.manageTermsAccept(activePage);

        case ErrorTypes.CLOSE_APP:
          return this.manageCloseAppError(activePage);

        case ErrorTypes.NOT_FOUND:
          return this.manageNotFoundException(activePage);

        case ErrorTypes.OTHER: // Other
          return this.manageOtherErrors(activePage);

        case ErrorTypes.ON_BOARDING_REQUIRED:
          return this.manageOnboardingRequired(activePage);
        default:
          return this.manageDefaultErrors(error, activePage);
      }
    });
  }

  #subscribeTours() {
    this.#tourUseCases.closeOutsideDisabled$.subscribe((closeOutsideDisabled) => {
      this.closeOutsideDisabled = closeOutsideDisabled;
      this.#cdr.detectChanges();
    });
  }

  manageMaintenanceMode(activePage: string) {
    if (activePage !== PAGES.MAINTENANCE) {
      this.appManagerService.setMaintenanceMode();
    }
  }

  private manageUserBanned(activePage: string) {
    if (activePage !== PAGES.BANNED) {
      this.appManagerService.setBannedStatus();
    }
  }

  private manageTermsAccept(activePage: string) {
    if (activePage !== PAGES.TERMS_AND_CONDITIONS_INTER) {
      this.appManagerService
        .redirect(NavigationEvents.Push, { path: PAGES.TERMS_AND_CONDITIONS_INTER })
        .catch((err) => this.logger.error(err));
    }
  }

  private manageCloseAppError(activePage: string) {
    if (activePage !== PAGES.CLOSE_APP) {
      this.appManagerService.redirect(NavigationEvents.Push, { path: PAGES.CLOSE_APP });
    }
  }

  private manageNotFoundException(activePage: string) {
    if (activePage !== PAGES.ERROR_404) {
      this.appManagerService
        .redirect(NavigationEvents.Push, { path: PAGES.ERROR_404 })
        .catch((err) => this.logger.error(err));
    }
  }

  private manageOtherErrors(activePage: string) {
    if (activePage !== PAGES.ERROR) {
      this.appManagerService
        .redirect(NavigationEvents.Push, { path: PAGES.ERROR })
        .catch((err) => this.logger.error(err));
    }
  }

  private manageOnboardingRequired(activePage: string) {
    if (activePage !== PAGES.ON_BOARDING) {
      this.appManagerService
        .redirect(NavigationEvents.Push, { path: PAGES.ON_BOARDING })
        .catch((err) => this.logger.error(err));
    }
  }

  private async manageDefaultErrors(error: AppError, activePage: string) {
    if (this.checkIsOfflineError(error)) {
      return this.manageOfflineError();
    } else if (error.isCritical()) {
      this.manageCriticalError(activePage);
    } else if (error.isAuthError()) {
      await this.manageAuthError(error);
    }

    return null;
  }

  private checkIsOfflineError(error: AppError): boolean {
    return (
      error?.type === ErrorTypes.OFFLINE ||
      error.hasOneOfMessages([ErrorMessages.NETWORK_ERROR, ErrorMessages.ADDRESS_UNREACHABLE])
    );
  }

  private manageOfflineError() {
    this.network.hasServerConnection().then((isOnline) => {
      if (!isOnline) {
        return window.location.reload();
      }
    });
  }

  private manageCriticalError(activePage: string) {
    if (activePage !== PAGES.ERROR) {
      this.appManagerService.setErrorStatus(true);
    }
  }

  private async manageAuthError(error: AppError) {
    // Auth
    if (error.isOfType(ErrorTypes.ERROR_LOGIN_SSO)) {
      await domChanges(500);
      return this.navigationHelperService.doRedirect(NavigationEvents.SetRoot, { path: PAGES.LOGOUT });
    }
    return this.appManagerService.redirect(NavigationEvents.SetRoot, { path: PAGES.LOGOUT });
  }

  private async onLogged() {
    const userInfo = await this.storageService.getUserData();
    const userHome = await this.storageService.getUserHome();
    const homeType = await this.storageService.getHomeType();
    const userLoginType = await this.storageService.getLoginType();

    if (!ENV.minimizeRealTimeRequests) {
      this.userSyncPropsService
        .syncAllProperties()
        .then((success) => this.logger.debug(success))
        .catch(() => this.network.redirectIfOffline());
    }

    this.language.setLanguage();

    if (homeType === HomeType.ONBOARDING) {
      this.logger.info('user must watch on boarding');
      return this.errorService.add(new AppError(ErrorTypes.ON_BOARDING_REQUIRED));
    }

    if (userLoginType === UserLoginType.ADFS) {
      this.#webSocketManagerService.initWebSocketManagement(userInfo.userid);
    }

    if (userHome === UserHome.DEFAULT || userHome === UserHome.EMPLOYEE) {
      NotificationHandler.init(
        this.navigationService,
        this.alertService,
        this.translateService,
        this.modalManager,
        this.ptrabSessionManager,
        this.appManagerService,
        this.loadingService,
        this.#notificationUseCases,
        this.#firebaseAnalyticsUseCases
      );

      this.showLightBoxCookies();
    }

    this.appManagerService.userInfoCompleted();

    if (this.redirectOnLoginChange) {
      await this.appManagerService.redirectAfterSuccessUserInfoInit(userInfo).catch((err) => this.logger.error(err));
    }

    this.checkIsExpiredCookies();
    this.#subscribeTours();
    this.#tourUseCases.initService(userInfo.walkthroughs, userInfo.news);

    return null;
  }

  onLogout() {
    this.logger.info('Logout');
    this.authService.logoutComplete();
    this.language.setLanguageFromBrowser();
    this.messageService.disconnect();
    this.errorService.reset();

    this.navigationService.navigate(new NavigationEvent(NavigationEvents.SetRoot, { path: PAGES.LOGOUT }));
  }

  private async determineRootPage(navParams = {}) {
    this.logger.debug(`determineRootPage: Location is ${JSON.stringify(location)}`);

    const path = this.getPath(navParams);
    this.logger.debug(`determineRootPage: getPath is "${path}"`);

    const isLogged = this.authService.isLoggedIn() || (await this.authService.hasSession());
    const rootPageHelper = new RootPageHelper(this.webViewService, path, ENV.guards.mobileweb, isLogged);

    const rootPage = rootPageHelper.getInitialInternalRoute(navParams);
    this.logger.debug(`determineRootPage: calculated root page is ${JSON.stringify(rootPage)}`);

    if (!rootPage.path) {
      this.logger.debug(
        `determineRootPage: Page refresh, direct link or other route not considered as rootPage: "${path}"`
      );
      rootPage.path = RouteHelper.getPageFromPath(path);
      await this.appManagerService.rootPageStablished(rootPage);
    } else {
      this.logger.debug(`determineRootPage: Redirect to "${JSON.stringify(rootPage)}"`);
      try {
        const navigationExtras: NavigationExtras = { state: rootPage.navParams };
        await this.appManagerService.rootPageStablished(rootPage);

        await this.router.navigateByUrl(rootPage.path, navigationExtras);
      } catch (error) {
        this.logger.error(error);
      }
    }

    if (isLogged && !RouteHelper.isPublic(rootPage)) {
      this.redirectOnLoginChange = false;
      this.logger.debug('determineRootPage: user was logged and is private path, init user info...');
      await this.authService.initUserInfo();
    }
  }

  private getPath(params: MSafeAny) {
    const hash = location.hash || params.hash;
    const path = this.location.path();
    return hash || path;
  }
}
